
import { observer } from 'mobx-react-lite'
import { useEffect } from 'react'

// evertel
import { Icon, FloatingMenu, MenuItem, MenuHeader } from '@evertel/web/ui'
import { HTMLAttributes } from 'react'
import { useService } from '@evertel/di'
import { CurrentUserController } from '@evertel/blue-user'
import { SessionService } from '@evertel/session/SessionService'
import { DepartmentController } from '@evertel/department'


interface HeaderButtonSettingsProps extends HTMLAttributes<HTMLButtonElement> {
    onClick?: () => void
}


const HeaderButtonSettings: React.FC<HeaderButtonSettingsProps> = observer(({
    ...otherProps
}) => {

    const currentUserController = useService(CurrentUserController, [])
    const sessionService = useService(SessionService, [])
    const departmentController = useService(DepartmentController, [])

    useEffect(() => {
        if (sessionService.selectedDepartmentId) {
            departmentController.init(sessionService.selectedDepartmentId as number)
        }
    }, [sessionService.selectedDepartmentId])

    const canManageAgency = currentUserController.canManage
    const isAdmin = currentUserController.isAdmin

    // Show the gear if user can manage this agency OR is an Evertel admin
    const showSettings = isAdmin || canManageAgency

    const renderEvertelAdministrationMenu = () => (
        <FloatingMenu label="Evertel Admin">
            <MenuHeader style={{ color: 'dodgerblue' }} label="Evertel Admin:" />
            <MenuItem
                label="Dashboard"
                icon={{ name: 'tachometer-alt' }}
                to="/admin/dashboard"
            />
            <MenuItem
                label="Agencies"
                icon={{ name: 'building' }}
                to="/admin/agencies"
            />
            <MenuItem
                label="Users"
                icon={{ name: 'users' }}
                to="/admin/users"
            />
            <MenuItem
                label="Bot Campaigns"
                icon={{ name: 'paper-plane' }}
                to="/admin/messaging"
            />
            <MenuItem
                label="Verified Domains"
                icon={{ name: 'cloud-check' }}
                to="/admin/verified-domains"
            />
            <MenuItem
                label="App Version Notices"
                icon={{ name: 'mobile-screen-button' }}
                to="/admin/app-versions"
            />
            <MenuItem
                label="Global EverDoc Templates"
                icon={{ name: 'table-layout' }}
                to="/everdocs/schema/templates/global"
            />
        </FloatingMenu>
    )

    const renderAdminToolsMenu = () => (
        <FloatingMenu label="Admin Links">
            <MenuHeader style={{ color: 'green' }} label="Admin Links:" />
            <MenuItem
                label="JIRA"
                icon={{ name: 'jira', type: 'brands' }}
                href="https://evertel.atlassian.net/"
            />
            <MenuItem
                label="Auth0/Okta"
                icon={{ name: 'shield-keyhole', type: 'solid' }}
                href="https://manage.auth0.com/dashboard/us/evertel-production/"
            />
            <MenuItem
                label="Mandrill"
                icon={{ name: 'mailchimp', type: 'brands' }}
                href="https://mandrillapp.com/"
            />
            <MenuItem
                label="Hubspot"
                icon={{ name: 'hubspot', type: 'brands' }}
                href="https://app.hubspot.com/"
            />
        </FloatingMenu>
    )

    const renderManageAgencyMenu = () => (
        <>
            <MenuHeader>
                <span style={{ fontWeight: 'bold' }}>Manage Agency:</span>
            </MenuHeader>

            <MenuItem
                label="Users"
                icon={{ name: 'users' }}
                to="/manage/users"
            />
            <MenuItem
                label="Rooms"
                icon={{ name: 'sitemap' }}
                to="/manage/rooms"
            />
            {currentUserController.canExecutive && (
                <>
                    <MenuItem
                        label="Audits"
                        icon={{ name: 'search' }}
                        to="/manage/audits"
                    />
                    <MenuItem
                        label="History Logs"
                        icon={{ name: 'history' }}
                        to="/manage/history"
                    />
                    <MenuItem
                        label="Usage Reports"
                        icon={{ name: 'chart-pie' }}
                        to="/manage/reports"
                    />
                    <MenuItem
                        label="Agency Settings"
                        icon={{ name: 'cogs' }}
                        to="/manage/agency/settings"
                    />
                    {departmentController.integrationsEnabled && (
                        <MenuItem
                            label="Connector Configuration"
                            icon={{ name: 'share-nodes' }}
                            to={`/manage/connectors`}
                        />
                    )}
                </>
            )}
        </>
    )


    return (
        // eslint-disable-next-line react/jsx-no-useless-fragment
        <>
            {showSettings && (
                <FloatingMenu
                    id="header-button-management-menu"
                    label="Management Menu"
                    title="Management Menu"
                    aria-label="Management Menu"
                    renderLabel={() => (
                        <Icon
                            name="cog"
                            size={16}
                        />
                    )}
                    {...otherProps}
                >
                    {isAdmin && renderEvertelAdministrationMenu()}
                    {isAdmin && renderAdminToolsMenu()}
                    {(canManageAgency || isAdmin) && renderManageAgencyMenu()}
                </FloatingMenu>
            )}
        </>
    )

})

export { HeaderButtonSettings }
